import { useCallback, useEffect, useState } from 'react';

/**
 * `useMeasuredHeight()` → `[ref, height]` — attach `ref` to an element and get
 * its live measured height in CSS pixels, or `null` before the first
 * measurement (and again if the element unmounts).
 *
 * ⚠️ `ref` IS A CALLBACK REF, not a ref object — there is no `.current` to
 * read. It has to be, so the hook can observe an element that does not exist
 * when the owning component mounts; see the note on the implementation.
 *
 * ── What this is FOR ──────────────────────────────────────────────────
 * Positioning a SECOND sticky thing beneath a FIRST one inside the same scroll
 * region. On the Gag Breakdown each group card has a sticky group title AND a
 * sticky column-header row; the second must pin at exactly the height of the
 * first, or it either overlaps the title or floats in a gap beneath it.
 *
 * 🔑 **Why measured and not the obvious arithmetic.** The group header is
 * `px-4 py-3` around a `text-sm` label, which computes to 44px — and writing
 * `44` here would be wrong the first time a long sub-department name wraps to
 * two lines, at exactly the width nobody tests. It is the same lesson
 * `useStickyToolbar` records for the toolbar (which wraps on narrow screens)
 * and that `OrderTableShell`'s `stickyTop` docblock states as a rule: **never a
 * guessed offset.**
 *
 * ── The BORDER box, not the content box ───────────────────────────────
 * 🪤 The sibling hook `useMeasuredWidth` reads `clientWidth`, deliberately — it
 * sizes a panel to a scrollport's VISIBLE CONTENT box, so it must exclude
 * borders and any scrollbar. This one is measuring how far to push a second
 * element DOWN past a first, which is the first element's full BORDER box.
 * `clientHeight` would omit its borders and leave the column headers overlapping
 * the group title by exactly the border width. **The two hooks read different
 * properties on purpose; do not "unify" them onto one.**
 *
 * ── 🚨 `Math.floor(getBoundingClientRect().height)`, NOT `Math.round(offsetHeight)` ──
 * Changed 2026-08-08 while chasing a reported 1px seam. Two separate reasons,
 * and the ROUNDING MODE is the one that matters:
 *
 * 1. **`getBoundingClientRect().height` is the FRACTIONAL truth; `offsetHeight`
 *    is already rounded to the nearest integer by the browser.** So a title
 *    whose real height is 43.6px reports `offsetHeight` **44** — and pinning the
 *    row below it at 44 leaves a 0.4px strip through which content scrolls.
 *    Reading the rect means we see 43.6 and can decide what to do with it.
 * 2. **FLOOR, never round.** The two failure directions are not symmetrical:
 *    rounding UP opens a visible gap, rounding DOWN produces an overlap that is
 *    absorbed by the upper element's padding and hidden by its higher z-index.
 *    **Always err into the overlap.** `Math.round` splits the difference and so
 *    opens a gap half the time.
 *
 * ⚠️ **`Math.round` is load-bearing in `useMeasuredWidth` for a DIFFERENT reason
 * — a measure → setState → re-render → re-measure oscillation — and that hazard
 * was re-checked here rather than assumed away.** It does not apply: nothing
 * this value feeds can resize the element being observed (it sets a `top` offset
 * on a SIBLING subtree, not a width on an ancestor), so there is no feedback
 * path. And `Math.floor` is idempotent on its own output regardless, so even if
 * a path appeared it could not oscillate — it would settle on the first integer.
 *
 * ── ResizeObserver, not just `window.resize` ──────────────────────────
 * The header's height changes without any window resize — a label wraps when the
 * sidebar expands, a group count grows a digit. The `resize` listener is kept
 * only as a fallback for environments with no ResizeObserver.
 *
 * ⚠️ **jsdom does no layout, so `getBoundingClientRect().height` is 0 there** —
 * the hook MEASURES SUCCESSFULLY, at zero, rather than staying `null`. Tests
 * therefore cannot tell a measured offset from a literal one; that is a browser
 * check.
 *
 * `null` until the first measurement lands, so a caller can render an unpinned
 * first paint rather than pinning at a wrong offset.
 *
 * ── Why this is a separate hook and not `useMeasuredSize` ─────────────
 * A combined hook would re-render every caller whenever EITHER axis changed, and
 * every caller here wants exactly one number: the panel sizing wants width and
 * knows nothing about height, this wants height and knows nothing about width.
 * Two small hooks that each do one thing beat one that makes both callers pay
 * for the other's axis.
 */
export default function useMeasuredHeight() {
  // 🚨 A CALLBACK REF KEPT IN STATE, NOT A `useRef` — AND THIS IS A BUG FIX,
  // NOT A STYLE PREFERENCE (2026-08-23).
  //
  // This was `useRef` + `useEffect(…, [])`. That effect runs ONCE, when the
  // OWNING COMPONENT mounts, and it read `ref.current` and attached the
  // ResizeObserver at that moment. **For a node that is conditionally
  // rendered, `ref.current` is still `null` then** — so `measure()` measured
  // nothing, no observer was ever attached, and the hook returned `null`
  // FOREVER. Nothing re-ran when the node finally appeared.
  //
  // 🪤 IT FAILED SILENTLY AND THE CALLER LOOKED FINE. `CrewGanttView`'s
  // level-2 details panel is mounted only once the user opens it, so it hit
  // exactly this: `detailsBodyHeight` stayed `null`, `detailsHeight` stayed
  // `0`, the row never grew, and the absolutely-positioned panel was painted
  // ON TOP OF the following crew rows. Measured in Chromium: with the panel
  // open the timeline row was 86px (42 + the 44px day strip) instead of 590,
  // and rows at y=601, 643 and 685 sat underneath a panel spanning 601→1105.
  // The whole client suite stayed green throughout — jsdom answers 0 to every
  // rect, so it cannot tell a measurement of zero from no measurement at all.
  //
  // A node-keyed effect re-runs whenever React hands over a different element
  // (mount, unmount, or replacement), which is the only way to observe
  // something that does not exist yet. The other caller — `GagGroupTable`'s
  // first group header — is unaffected either way: that table renders only
  // when `visibleGags.length > 0`, so its node exists in the first commit and
  // the old code happened to catch it.
  const [node, setNode] = useState(null);
  const ref = useCallback((el) => setNode(el), []);
  const [height, setHeight] = useState(null);

  useEffect(() => {
    if (!node) {
      // The element went away. Report "unmeasured" rather than a stale height
      // from the element that used to be there.
      setHeight(null);
      return undefined;
    }

    const measure = () => {
      // FLOOR the fractional truth — see the docblock. Rounding up opens a
      // visible gap; rounding down produces a harmless hidden overlap.
      setHeight(Math.floor(node.getBoundingClientRect().height));
    };
    measure();

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(node);
    }
    window.addEventListener('resize', measure);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [node]);

  return [ref, height];
}
